import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import PortfolioHeading from '../../../../components/PortfolioHeading';
import useQueryParams from 'hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import PortfolioDealsEmptyState from 'components/PortfolioDealsEmptyState';
import PortfolioFilterBar from '../../../../components/PortfolioFilterBar/PortfolioFilterBar.component';
import {
  BuyerListingStats,
  PortfolioQueryParams,
} from '../../../../components/PortfolioFilterBar/models';
import {
  buyerFilterOptions,
  BUYING_ALL_LISTINGS_QUERY,
  COUNT_VALUES_MAP,
  MORE_ACTIONS,
} from './utils';
import { aggregateStats } from '../../../../utils';
import {
  TABLE_COLUMNS as BUYER_TABLE_COLUMNS,
  HEADING_TRANSLATIONS as BUYER_HEADING_TRANSLATIONS,
} from './components/BuyerDeals/components/BuyerDealTableCells/data';
import { useNavigate } from 'react-router-dom';
import {
  PF_GRID_VIEW_LIMIT,
  BUYING_DEFAULT_SORT,
  PRIVATE_DEFAULT_SORT,
} from '../../../data';
import { ViewSelect } from '../../../../components/ViewSelect';
import { MoreDropdown } from '../../../../components/MoreDropdown';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { ManageListViewDrawer } from '../../../../components/ManageListViewDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { HIDE_TABLE_COLUMNS } from 'models/User';
import useSearchParams from 'hooks/useSearchParams';
import DataRoomInvitations from './components/DataRoomInvitations';
import ProspectiveBuyer from './components/ProspectiveBuyer';
import {
  TABLE_COLUMNS as DR_TABLE_COLUMNS,
  HEADING_TRANSLATIONS as DR_HEADING_TRANSLATIONS,
} from './components/DataRoomInvitations/components/DataRoomInvitationsTableCells/data';
import {
  TABLE_COLUMNS as PRIVATE_TABLE_COLUMNS,
  HEADING_TRANSLATIONS as PRIVATE_HEADING_TRANSLATIONS,
} from './components/ProspectiveBuyer/components/ProspectiveBuyersTableCells/data';
import { useLockScrollOnReroute } from 'hooks/useLockScrollOnReroute';
import BuyerDeals from './components/BuyerDeals';
import { Button } from 'ncoded-component-library';
import GridIcon from 'icons/Grid.icon';
import BurgerIcon from 'icons/Burger.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import ColumnsIcon from 'icons/Columns.icon';
import useRefetch from 'hooks/useRefetch';
import {
  DR_INVITATION_PAGE,
  PRIVATE_INVITATION_PAGE,
  REQUIRED_COLUMNS,
} from './components/TableCells/data';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import { PAGE_PARAM_KEYWORD } from 'data/global';

import './BuyerBrowsePortfolio.styles.scss';
import './BuyerBrowsePortfolio.styles.responsive.scss';

const ALL_TABLE_COLUMNS = Array.from(
  new Set([
    ...BUYER_TABLE_COLUMNS,
    ...DR_TABLE_COLUMNS,
    ...PRIVATE_TABLE_COLUMNS,
  ]),
);

export const ALL_HEADING_TRANSLATIONS = {
  ...BUYER_HEADING_TRANSLATIONS,
  ...DR_HEADING_TRANSLATIONS,
  ...PRIVATE_HEADING_TRANSLATIONS,
};

type BuyerBrowsePortfolioProps = {
  className?: string;
};

const BuyerBrowsePortfolio: React.FC<BuyerBrowsePortfolioProps> = (props) => {
  const { className } = props;

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    currentUser: { isGridViewForDeals, hideBuyerPortfolioColumns },
    isRWCMember,
    updateView,
  } = useContext(CurrentUserContext);

  const [isEmptyDeals, setIsEmptyDeals] = useState(false);
  const [isEmptyDRInvitation, setIsEmptyDRInvitation] = useState(false);
  const [isEmptyPrivateInvitations, setIsEmptyPrivateInvitations] =
    useState(false);
  const [buyerListingStats, setBuyerListingStats] =
    useState<BuyerListingStats>();
  const [manageListDrawer, manageListDrawerRef] =
    useCallbackRef<SideDrawerRef>(null);

  const { refetchFlag, refetch } = useRefetch();

  const { limit, updateLimit } = useNumberOfResultsPerPage({
    pageParam: [
      PAGE_PARAM_KEYWORD,
      PRIVATE_INVITATION_PAGE,
      DR_INVITATION_PAGE,
    ],
  });

  const classes = classNames('buyer-browse-portfolio', className);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setMultipleQueryParams } = useQueryParams<PortfolioQueryParams>();
  const searchParams = useSearchParams();

  const isPhablet = useMatchMedia('isPhablet');

  useLockScrollOnReroute();

  const isTableView = useMemo(
    () => !isGridViewForDeals && !isPhablet,
    [isGridViewForDeals, isPhablet],
  );

  const isEmptyPage = useMemo(
    () => isEmptyDeals && isEmptyDRInvitation && isEmptyPrivateInvitations,
    [isEmptyDRInvitation, isEmptyDeals, isEmptyPrivateInvitations],
  );

  const dataLimit = useMemo(
    () => `${isTableView ? limit : PF_GRID_VIEW_LIMIT}`,
    [isTableView, limit],
  );

  useEffect(() => {
    !searchParams.$or &&
      !searchParams.$sort &&
      !searchParams.$drInvitationSort &&
      !searchParams.$privateInvitationSort &&
      setMultipleQueryParams(
        {
          or: BUYING_ALL_LISTINGS_QUERY,
          sort: BUYING_DEFAULT_SORT,
          drInvitationSort: PRIVATE_DEFAULT_SORT,
          privateInvitationSort: PRIVATE_DEFAULT_SORT,
        },
        true,
      );
  }, [searchParams, setMultipleQueryParams]);

  useEffect(() => {
    (async () => {
      try {
        const stats = await api.portfolio.buyer.buyerListingStats();

        const newStats = {
          ...stats.data,
          all: `${Object.values(stats.data).reduce(
            (acc, el) => acc + +el,
            -stats.data.totalWithdrawnListings,
          )}`,
        };

        setBuyerListingStats(newStats);
      } catch (err) {
        popServerError(err);
      }
    })();
  }, [refetchFlag, popServerError]);

  const buyerTriggerText = useMemo(
    () =>
      searchParams.$or
        ? t(
            buyerFilterOptions.find(
              (option) => option.value === searchParams.$or,
            )?.label,
          )
        : '',
    [searchParams, t],
  );

  const openManageList = useCallback(
    () => manageListDrawer?.setOpen(true),
    [manageListDrawer],
  );

  const moreOptions = useMemo(
    () => [
      ...MORE_ACTIONS,
      ...(!isPhablet
        ? [
            {
              label: t(
                isGridViewForDeals ? 'switchToTableView' : 'switchToCardView',
              ),
              icon: isGridViewForDeals ? BurgerIcon : GridIcon,
              onClick: () =>
                updateView('isGridViewForDeals', !isGridViewForDeals),
            },
            {
              label: t('editTableView'),
              icon: ColumnsIcon,
              onClick: openManageList,
            },
          ]
        : []),
    ],
    [isGridViewForDeals, isPhablet, t, updateView, openManageList],
  );

  const manageTableOptions = useMemo(
    () =>
      ALL_TABLE_COLUMNS.map((el) => ({
        value: el,
        enabled: !REQUIRED_COLUMNS.includes(el),
        label: ALL_HEADING_TRANSLATIONS[el] || t(el),
      })),
    [t],
  );

  const buyerListingCountForValue = (
    value: string,
    buyerListingStats: BuyerListingStats,
  ) => {
    return `${
      buyerListingStats
        ? value
          ? buyerListingStats[COUNT_VALUES_MAP[value]]
          : +aggregateStats(buyerListingStats)
        : ''
    }`;
  };

  return (
    <div className={classes}>
      <div className="browse-portfolio-heading">
        <div className="browse-portfolio-heading__left">
          <PortfolioHeading />

          {!isRWCMember && (
            <PortfolioFilterBar
              filterOptions={buyerFilterOptions}
              filterTriggerText={buyerTriggerText}
              listingStats={buyerListingStats}
              listingCountForValue={buyerListingCountForValue}
            />
          )}
        </div>

        <div className="browse-portfolio-heading__right">
          {isTableView && (
            <NumberOfResultsPerPageFilter
              limit={limit}
              updateLimit={updateLimit}
            />
          )}
          {!isPhablet && (
            <>
              <ViewSelect
                updateProp="isGridViewForDeals"
                isTableView={isTableView}
              />
              {!isRWCMember &&
                MORE_ACTIONS.map(({ to, label, icon }) => (
                  <Button key={to} icon={icon} onClick={() => navigate(to)}>
                    {t(label)}
                  </Button>
                ))}
            </>
          )}

          {!isRWCMember && <MoreDropdown options={moreOptions} />}
          <ManageListViewDrawer
            ref={manageListDrawerRef}
            initialHideValues={hideBuyerPortfolioColumns}
            updateProp={HIDE_TABLE_COLUMNS.BUYER}
            options={manageTableOptions}
          />
        </div>
      </div>

      <BuyerDeals
        isTableView={isTableView}
        dataLimit={dataLimit}
        isEmpty={isEmptyDeals}
        setIsEmptyDeals={setIsEmptyDeals}
        openManageList={openManageList}
        refetch={refetch}
      />

      <ProspectiveBuyer
        isTableView={isTableView}
        dataLimit={dataLimit}
        hideColumns={hideBuyerPortfolioColumns}
        isEmpty={isEmptyPrivateInvitations}
        setIsEmptyPrivateInvitations={setIsEmptyPrivateInvitations}
      />

      <DataRoomInvitations
        isTableView={isTableView}
        dataLimit={dataLimit}
        hideColumns={hideBuyerPortfolioColumns}
        isEmpty={isEmptyDRInvitation}
        setIsEmptyDRInvitation={setIsEmptyDRInvitation}
      />

      {isEmptyPage && <PortfolioDealsEmptyState isBuyerSide />}
    </div>
  );
};

export default BuyerBrowsePortfolio;
